import React, { useState, useEffect } from "react";
import { onAuthChange, getCurrentUser } from "../firebase/auth";
import { getDocumentById, updateDocument } from "../firebase/firestore";
import AuthContext from "./AuthContext";

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthChange(async (authUser) => {
            try {
                if (authUser) {
                    setUser(authUser);
                    const profile = await getDocumentById("users", authUser.uid);
                    if (profile) {
                        setUserProfile(profile);
                    } else {
                        console.warn("User profile not found in Firestore for uid:", authUser.uid);
                        setUserProfile(null);
                    }
                } else {
                    setUser(null);
                    setUserProfile(null);
                }
            } catch (err) {
                console.error("Error in auth state change:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    const logout = async () => {
        try {
            const { logoutUser } = await import("../firebase/auth");
            await logoutUser();
            setUser(null);
            setUserProfile(null);
            setError(null);
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const updateUserProfile = async (updates) => {
        if (!user?.uid) {
            throw new Error("No user logged in");
        }

        try {
            await updateDocument("users", user.uid, {
                ...updates,
                updatedAt: new Date()
            });

            setUserProfile((prev) => ({
                ...prev,
                ...updates,
                updatedAt: new Date()
            }));

            return true;
        } catch (err) {
            console.error("Error updating user profile:", err);
            throw new Error(`Failed to update profile: ${err.message}`);
        }
    };

    const value = {
        user,
        userProfile,
        loading,
        error,
        logout,
        updateUserProfile,
        isAdmin: userProfile?.role === "admin",
        isStudent: userProfile?.role === "student",
        isStartup: userProfile?.role === "startup"
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
